import { User } from "oidc-client-ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { doDelete, doGet } from "./util/apiHelper.ts";
import { getConfig } from "../services/configService";
import { getValidAccessToken } from "../auth/userManager";
import { MEDIA_QUERY_KEYS } from "./util/queryKeys.ts";

export interface MediaCreationDto {
  description: string;
  media: Blob;
}

export interface MediaDto {
  id: string;
  uri: string;
  description: string;
  createdAt: string;
}

export interface MediaResponseDto {
  media: MediaDto[];
}

const getMedia = async (
  userId: string,
  user: User,
): Promise<MediaResponseDto> => {
  return await doGet<MediaResponseDto>(`/teachers/${userId}/media`, user);
};

const deleteMedia = async (
  mediaId: string,
  user: User,
): Promise<MediaResponseDto> => {
  return await doDelete<MediaResponseDto>(
    `/teachers/${user.profile.sub}/media/${mediaId}`,
    user,
  );
};

export const postMedia = async (
  mediaCreationDto: MediaCreationDto,
  user: User,
): Promise<void> => {
  if (user == null) {
    throw new Error("User not present");
  }
  const config = await getConfig();
  const token = await getValidAccessToken();
  const formData = new FormData();
  formData.append("media", mediaCreationDto.media);
  formData.append("description", mediaCreationDto.description);
  const response = await fetch(
    `${config.api.baseUrl}/teachers/${user.profile.sub}/media`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to post profile picture");
  }
};

export const useMediaQuery = (
  userId?: string,
  options?: { enabled: boolean },
) => {
  return useAuthenticatedQuery(
    [...MEDIA_QUERY_KEYS.media, userId],
    async (user) => {
      if (userId) {
        return await getMedia(userId, user);
      }
    },
    { ...options },
  );
};

export const useDeleteMediaMutation = () => {
  return useAuthenticatedMutation(
    (mediaId: string, user) => deleteMedia(mediaId, user),
    { invalidateKeys: [MEDIA_QUERY_KEYS.media] },
  );
};

export const usePostMediaMutation = () => {
  return useAuthenticatedMutation(
    (mediaCreationDto: MediaCreationDto, user) =>
      postMedia(mediaCreationDto, user),
    { invalidateKeys: [MEDIA_QUERY_KEYS.media] },
  );
};
